/* DOM-free utilities (safe to import from node tests). */

export const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

let _uid = 1;
export const uid = (p: string) => p + (_uid++) + '_' + Math.random().toString(36).slice(2, 6);

export const deg = (r: number) => r * 180 / Math.PI;
export const rad = (d: number) => d * Math.PI / 180;

/** Absolute angular difference folded into [0, π]. */
export const angDiff = (a: number) => Math.abs(Math.atan2(Math.sin(a), Math.cos(a)));

/* ---- units: the doc is always inches; display and typed input follow the setting ---- */
export type Units = 'ft' | 'm';
let units: Units = 'ft';
export const getUnits = (): Units => units;
export const setUnits = (u: Units): void => { units = u; };
/** inches per centimetre */
export const IN_PER_CM = 1 / 2.54;

function fmtMetric(inches: number): string {
  const neg = inches < -1e-6;
  const mm = Math.round(Math.abs(inches) * 25.4);   // decide on the rounded value: 99.98 cm reads "1.00 m", not "100 cm"
  const s = mm < 1000 ? `${(mm / 10).toString()} cm` : `${(Math.round(mm / 10) / 100).toFixed(2)} m`;
  return (neg ? '−' : '') + s;
}

/** Area for display: "120 sq ft" or "11.1 m²" (input in square inches). */
export function fmtArea(sqin: number): string {
  return units === 'm' ? `${(sqin * 6.4516 / 10000).toFixed(1)} m²` : `${Math.round(sqin / 144)} sq ft`;
}

export function fmtLen(inches: number): string {
  if (units === 'm') return fmtMetric(inches);
  const neg = inches < -1e-6; inches = Math.abs(inches);
  let whole = Math.floor(inches + 1e-6);
  let e = Math.round((inches - whole) * 8);
  if (e === 8) { whole++; e = 0; }
  const fr = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'][e];
  const ft = Math.floor(whole / 12), inch = whole % 12;
  let s = '';
  if (ft) s += ft + '′';
  if (inch || fr || !ft) s += (ft ? ' ' : '') + inch + fr + '″';
  return (neg ? '−' : '') + s;
}

/** fmtLen with plain ascii quotes — what we prefill into editable inputs. */
export const plain = (v: number) => fmtLen(v).replace(/[′″]/g, m => m === '′' ? "'" : '"');

// parse "10'6", "10' 6 1/2", "126", "6\"", "3.5 m", "350cm", "3500 mm" ... bareUnit: 'in' | 'ft'
// (a bare number is centimetres when the units setting is metric)
export function parseLen(str: string | null | undefined, bareUnit: 'in' | 'ft'): number | null {
  if (str == null) return null;
  str = String(str).trim();
  const metric = str.match(/^(-?\d+(?:[.,]\d+)?)\s*(mm|cm|m)$/i);
  if (metric) {
    const v = parseFloat(metric[1].replace(',', '.')), k = metric[2].toLowerCase();
    return v * (k === 'mm' ? 0.1 : k === 'cm' ? 1 : 100) * IN_PER_CM;
  }
  str = str.replace(/[”″]/g, '"').replace(/[’′]/g, "'").replace(/ft/gi, "'").replace(/in\b/gi, '"');
  const UF: Record<string, string> = { '⅛': ' 1/8', '¼': ' 1/4', '⅜': ' 3/8', '½': ' 1/2', '⅝': ' 5/8', '¾': ' 3/4', '⅞': ' 7/8' };
  str = str.replace(/[⅛¼⅜½⅝¾⅞]/g, m => UF[m]).replace(/\s+/g, ' ');
  if (!str) return null;
  const frac = (s: string): number | null => { // "6", "6.5", "6 1/2", "1/2"
    s = s.trim(); if (!s) return 0;
    const m = s.match(/^(\d+(?:\.\d+)?)?\s*(?:(\d+)\s*\/\s*(\d+))?$/);
    if (!m || (m[1] === undefined && m[2] === undefined)) return null;
    return (m[1] ? parseFloat(m[1]) : 0) + (m[2] ? parseInt(m[2]) / parseInt(m[3]!) : 0);
  };
  let m = str.match(/^(.*?)'\s*(.*?)"?\s*$/); // has feet marker
  if (m) {
    const f = frac(m[1]), i = frac(m[2]);
    if (f === null || i === null) return null;
    return f * 12 + i;
  }
  m = str.match(/^(.*?)"\s*$/);
  if (m) { const i = frac(m[1]); return i === null ? null : i; }
  const v = frac(str.replace(',', '.'));
  if (v === null) return null;
  if (units === 'm') return v * IN_PER_CM;
  return bareUnit === 'ft' ? v * 12 : v;
}
